// ItemCustomizationModal.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  IconButton, RadioGroup, FormControlLabel, Radio, Checkbox, FormGroup, Box
} from '@mui/material';
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';
import {
  BaseItem, MenuItem, ItemVariant, IngredientOption, FullMenuPageData, ComboVariant, BaseItemVariants, BaseItemOptions,
  findBaseItemAndVariant
} from '../../models/fullMenuPageData';
import { calculateCartItemPrice, computeBaseItemSelectionPriceVariation, getLowestPriceOfMenuItem, MenuCartItem, cheapestPriceOfComboVariant, SelectedIngredientOption, SelectedMenuItems, toMenuCart, getPriceOfMenuCart } from '../../models/fullMenuCart';
import { formatMoney } from '../../services/strings/formatters';
import { ShowAsSoldOut } from '../../shared/showAsSoldOut';

interface ItemCustomizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddToCart: (item: MenuCartItem[]) => void;
  baseItem: BaseItem;
  menuItem: MenuItem;
  comboVariant: ComboVariant | null;
  menuData: FullMenuPageData;
}

/**
 * Flatten option groups into SelectedIngredientOption[] for state initialization.
 */
function flattenOptionsToselectedIngredientOptions(optGroups: BaseItemOptions[] | undefined): SelectedIngredientOption[] {
  if (!optGroups || optGroups.length === 0) return [];
  const out: SelectedIngredientOption[] = [];
  for (const g of optGroups) {
    for (const o of g.options) {
      out.push({ option: o, quantity: 0 });
    }
  }
  return out;
}

/**
 * Manage rendering of a single base item's variant groups and option groups.
 *
 * - selectedVariantsByGroup: Record<groupId, ItemVariant | undefined>
 * - setSelectedVariantForGroup(groupId, variant)
 * - selectedIngredientOptions: SelectedIngredientOption[] (flattened across option groups)
 * - handleIngredientChange(optionId, checked)
 */
function manageIndividualItem(
  baseItem: BaseItem,
  selectedVariantsByGroup: Record<string, ItemVariant | undefined>,
  setSelectedVariantForGroup: (groupId: string, v: ItemVariant | undefined) => void,
  selectedIngredientOptions: SelectedIngredientOption[],
  handleIngredientChange: (ingredientId: string, checked: boolean) => void,
  menuItem: MenuItem
) {
  return <>
    {/* Variant Groups */}
    {baseItem.variants.map((group: BaseItemVariants) => (
      <Box key={group.id} sx={{ mb: 1 }}>
        <Typography variant="subtitle1">{group.name}</Typography>
        <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>{group.description}</Typography>
        <RadioGroup
          value={selectedVariantsByGroup[group.id]?.id || ''}
          onChange={(e) => {
            const chosen = group.variants.find(v => v.id === e.target.value);
            setSelectedVariantForGroup(group.id, chosen);
          }}
        >
          {group.variants.map(variant => {
            if (!variant.isSoldOut) {
              return <FormControlLabel
                key={variant.id}
                value={variant.id}
                control={<Radio />}
                label={`${variant.name} - (${formatMoney((menuItem.isCombo ? variant.comboPrice : variant.price) / 100)})`}
              />
            }
            return <Typography variant="subtitle1" sx={{ textDecoration: 'line-through' }}>
              {`X ${variant.name} - (${formatMoney((menuItem.isCombo ? variant.comboPrice : variant.price) / 100)}) - sold out`}</Typography>
          }
          )}
        </RadioGroup>
      </Box>
    ))}

    {/* Option Groups */}
    {baseItem.options.map((group: BaseItemOptions) => (
      <Box key={group.id} sx={{ mt: 2 }}>
        <Typography variant="subtitle1">{group.name}</Typography>
        <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>{group.description}</Typography>
        <FormGroup>
          {group.options.map((option: IngredientOption) => {
            if (!option.isSoldOut) {
              return <FormControlLabel
                key={option.id}
                control={
                  <Checkbox
                    checked={selectedIngredientOptions.some(si => si.option.id === option.id && si.quantity > 0)}
                    onChange={(e) => handleIngredientChange(option.id, e.target.checked)}
                  />
                }
                label={`${option.name} (+${formatMoney(option.price / 100)})`}
              />
            }
            return <Typography variant="subtitle1" sx={{ textDecoration: 'line-through' }}>
              {`X ${option.name} (+${formatMoney(option.price / 100)}) - sold out`}</Typography>
          })}
        </FormGroup>
      </Box>
    ))}
  </>;
}

/**
 * Renders sub-items for a comboVariant.
 * comboSelections keyed by baseItem.id, each entry has:
 *  - variantsByGroupId: Record<groupId, ItemVariant | undefined>
 *  - ingredients: SelectedIngredientOption[]
 */
function manageComboVariant(
  comboVariant: ComboVariant,
  menuData: FullMenuPageData,
  comboSelections: Record<string, { variantsByGroupId: Record<string, ItemVariant | undefined>; ingredients: SelectedIngredientOption[] }>,
  handleComboSetVariant: (itemId: string, groupId: string, variant: ItemVariant | undefined) => void,
  handleComboIngredientChange: (itemId: string, ingredientId: string, checked: boolean) => void,
  menuItem: MenuItem
) {
  return <>
    {comboVariant.itemVariantIds.map(ivID => {
      const resolved = findBaseItemAndVariant(menuData, ivID);
      const item = resolved.baseItem;
      if (!item) return null;
      const sel = comboSelections[item.id];
      return (
        <Box key={item.id} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: "row", justifyContent: "space-between" }}>
            <Typography variant="h6" sx={{ color: "#162C1F" }}>{item.name}</Typography>
            {item.imageUrl && <img src={item.imageUrl} alt="Preview" style={{ width: '128px' }} />}
          </Box>
          {manageIndividualItem(
            item,
            sel?.variantsByGroupId || {},
            (groupId, v) => handleComboSetVariant(item.id, groupId, v),
            sel?.ingredients || flattenOptionsToselectedIngredientOptions(item.options),
            (ingredientId, checked) => handleComboIngredientChange(item.id, ingredientId, checked),
            menuItem
          )}
        </Box>
      );
    })}
  </>;
}

export function ItemCustomizationModal({
  isOpen, onClose, onAddToCart, baseItem, menuItem, comboVariant, menuData
}: ItemCustomizationModalProps) {
  const [quantity, setQuantity] = useState(1);

  // SINGLE item state: selected variants per group, flattened selected ingredients
  const [selectedVariantsByGroup, setSelectedVariantsByGroup] = useState<Record<string, ItemVariant | undefined>>({});
  const [selectedIngredientOptions, setselectedIngredientOptions] = useState<SelectedIngredientOption[]>([]);

  // COMBO selections keyed by baseItem.id:
  // { [baseItemId]: { variantsByGroupId: { [groupId]: ItemVariant }, ingredients: SelectedIngredientOption[] } }
  const [comboSelections, setComboSelections] = useState<Record<string, { variantsByGroupId: Record<string, ItemVariant | undefined>; ingredients: SelectedIngredientOption[] }>>({});

  // Initialize/reset on open or when item/combo changes
  useEffect(() => {
    if (!isOpen) return;

    setQuantity(1);

    // Single item - build defaults: pick first variant in each group
    const singleVariantsInit: Record<string, ItemVariant | undefined> = {};
    for (const g of baseItem.variants) {
      singleVariantsInit[g.id] = g.variants?.[0];
    }
    setSelectedVariantsByGroup(singleVariantsInit);

    // Single item - flattened ingredients
    setselectedIngredientOptions(flattenOptionsToselectedIngredientOptions(baseItem.options));

    // Combo initialization: for each entry in comboVariant.itemVariantIds, resolve baseItem and pre-populate its group selections
    if (menuItem?.isCombo && comboVariant) {
      const csInit: Record<string, { variantsByGroupId: Record<string, ItemVariant | undefined>; ingredients: SelectedIngredientOption[] }> = {};
      for (const ivID of comboVariant.itemVariantIds) {
        const resolved = findBaseItemAndVariant(menuData, ivID);
        const item = resolved.baseItem;
        if (!item) continue;

        // prepare variantsByGroupId: default to first variant in each group
        const vb: Record<string, ItemVariant | undefined> = {};
        for (const g of item.variants) {
          vb[g.id] = g.variants?.[0];
        }

        // If ivID pointed to a specific variant, set the correct group -> variant
        if (resolved.variant && resolved.groupId) {
          vb[resolved.groupId] = resolved.variant;
        }

        csInit[item.id] = {
          variantsByGroupId: vb,
          ingredients: flattenOptionsToselectedIngredientOptions(item.options)
        };
      }
      setComboSelections(csInit);
    } else {
      setComboSelections({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, baseItem?.id, menuItem?.id, comboVariant?.id]);

  // Single handlers
  const setSelectedVariantForGroup = (groupId: string, variant: ItemVariant | undefined) => {
    setSelectedVariantsByGroup(prev => ({ ...prev, [groupId]: variant }));
  };

  const handleIngredientChange = (ingredientId: string, checked: boolean) => {
    setselectedIngredientOptions(prev => prev.map(ing => ing.option.id === ingredientId ? { ...ing, quantity: checked ? 1 : 0 } : ing));
  };

  // Combo handlers
  const handleComboSetVariant = (itemId: string, groupId: string, variant: ItemVariant | undefined) => {
    setComboSelections(prev => {
      const cur = prev[itemId] || { variantsByGroupId: {}, ingredients: [] };
      return {
        ...prev,
        [itemId]: {
          ...cur,
          variantsByGroupId: { ...cur.variantsByGroupId, [groupId]: variant }
        }
      };
    });
  };

  const handleComboIngredientChange = (itemId: string, ingredientId: string, checked: boolean) => {
    setComboSelections(prev => {
      const cur = prev[itemId];
      if (!cur) return prev;
      const nextIngredients = cur.ingredients.map(ing => ing.option.id === ingredientId ? { ...ing, quantity: checked ? 1 : 0 } : ing);
      return { ...prev, [itemId]: { ...cur, ingredients: nextIngredients } };
    });
  };

  const getMenuCartItems = () => {
    const cart: MenuCartItem[] = []
    const cartItem = toMenuCart({
      baseItem,
      comboSelections,
      comboVariant,
      menuData,
      menuItem,
      selectedIngredientOptions,
      selectedVariantsByGroup
    })
    for (let i = 0; i < quantity; i++) {
      cart.push(cartItem)
    }
    return cart
  }

  // total price calculation
  const totalPrice = useMemo(() => {
    const cartItems = getMenuCartItems()
    if (!cartItems) return 0
    return getPriceOfMenuCart(cartItems, menuData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItem?.id, comboVariant?.id, comboSelections, selectedVariantsByGroup, selectedIngredientOptions, quantity]);



  // Build cart payload
  const handleAddToCart = () => {
    const cartItems = getMenuCartItems()
    onAddToCart(cartItems);
    onClose();
  };

  const headerImage = menuItem.imageUrl ? menuItem.imageUrl :
    baseItem.imageUrl ? baseItem.imageUrl : undefined

  return (
    <Dialog open={isOpen} onClose={onClose} scroll="body" fullWidth maxWidth="sm">
      <DialogTitle>

        {headerImage &&
          <img src={headerImage} alt="Preview" style={{ width: '100%' }} />}
        {baseItem.name} {comboVariant && " - " + comboVariant.name} {comboVariant ? "- " + formatMoney(cheapestPriceOfComboVariant(menuData, comboVariant) / 100) : formatMoney(getLowestPriceOfMenuItem(menuItem, menuData) / 100)}
      </DialogTitle>

      <DialogContent dividers>

        {menuItem.isCombo && comboVariant ?
          manageComboVariant(
            comboVariant,
            menuData,
            comboSelections,
            handleComboSetVariant,
            handleComboIngredientChange,
            menuItem
          )
          :
          manageIndividualItem(
            baseItem,
            selectedVariantsByGroup,
            setSelectedVariantForGroup,
            selectedIngredientOptions,
            handleIngredientChange,
            menuItem)
        }

        {/* Quantity Selection */}
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <IconButton onClick={() => setQuantity(q => Math.max(1, q - 1))}>
            <RemoveCircleOutline />
          </IconButton>
          <Typography sx={{ mx: 2 }}>{quantity}</Typography>
          <IconButton onClick={() => setQuantity(q => q + 1)}>
            <AddCircleOutline />
          </IconButton>
        </Box>

      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button
          sx={{ margin: "0 8px", border: "1px solid #162C1F", borderRadius: "24px" }}
          onClick={onClose}>
          <Typography variant="button" component="h1" sx={{ color: "#162C1F" }}>
            Cancel
          </Typography>
        </Button>
        <Button
          sx={{ margin: "0 8px", background: "#162C1F", border: "1px solid #162C1F", borderRadius: '24px' }}
          onClick={handleAddToCart} variant="contained" color="primary">
          Add to Cart - {formatMoney(totalPrice / 100)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
